/**
 * This file contains authentication parameters. Contents of this file
 * is roughly the same across other MSAL.js libraries. These parameters
 * are used to initialize Angular and MSAL Angular configurations in
 * in app.module.ts file.
 */

import { LogLevel, Configuration, BrowserCacheLocation } from '@azure/msal-browser';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const msalConfig: Configuration = {
  auth: {
    // clientId: '722fb839-7dc2-46be-a77a-040e2d2fd312', // tenant ID inforius.be: 28287824-cd58-4217-b482-3a73d4b49829
    clientId: JSON.parse(sessionStorage.getItem('office365'))?.clientId, // tenant ID inforius.be: 28287824-cd58-4217-b482-3a73d4b49829
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: '/private/ic/courriers-365', // Points to window.location.origin by default. You must register this URI on Azure portal/App Registration.
    postLogoutRedirectUri: '/', // Points to window.location.origin by default.
    clientCapabilities: ['CP1'], // This lets the resource server know that this client can handle claim challenges.
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: isIE, // Set this to "true" if you are having issues on IE11 or Edge. Remove this line to use Angular Universal
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Verbose,
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  graphMe: {
    path: '/me',
    endpoint: 'https://graph.microsoft.com/v1.0/me',
    scopes: ['user.Read'],
  },
  graphContacts: {
    path: '/me/contacts',
    endpoint: 'https://graph.microsoft.com/v1.0/me/contacts',
    scopes: ['contacts.Read'],
  },
  graphInboxMessages: {
    path: '/me/mailFolders/inbox/messages',
    endpoint: 'https://graph.microsoft.com/v1.0/me/mailFolders/inbox/messages',
    scopes: ['mail.read'],
  },
  graphOutboxMessages: {
    path: '/me/mailFolders/outbox/messages',
    endpoint: 'https://graph.microsoft.com/v1.0/me/mailFolders/outbox/messages',
    scopes: ['mail.read'],
  },
  graphMailFolders: {
    path: '/me/mailFolders',
    endpoint: 'https://graph.microsoft.com/v1.0/me/mailFolders',
    scopes: ['mail.read'],
  },
  graphMailFolderByID: {
    path: '/me/mailFolders/$ID/messages',
    endpoint: 'https://graph.microsoft.com/v1.0/me/mailFolders/$ID/messages',
    scopes: ['mail.read'],
  },
  graphSharedMailFolders: {
    path: '/users/$EMAIL/mailFolders',
    endpoint: 'https://graph.microsoft.com/v1.0/users/$EMAIL/mailFolders',
    scopes: ['mail.read.shared'],
  },
  graphSharedMailFolderByID: {
    path: '/users/$EMAIL/mailFolders/$ID/messages',
    endpoint: 'https://graph.microsoft.com/v1.0/users/$EMAIL/mailFolders/$ID/messages',
    scopes: ['mail.read.shared'],
  },
  getMailMINEShared: {
    path: '/users/$EMAIL/messages/$ID/$value',
    endpoint: 'https://graph.microsoft.com/v1.0/users/$EMAIL/messages/$ID/$value',
    scopes: ['mail.read.shared'],
  },
  getMailMessage: {
    path: '/me/messages/$ID',
    endpoint: 'https://graph.microsoft.com/v1.0/me/messages/$ID',
    scopes: ['mail.read'],
  },
  getMailMINE: {
    path: '/me/messages/$ID/$value',
    endpoint: 'https://graph.microsoft.com/v1.0/me/messages/$ID/$value',
    scopes: ['mail.read'],
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: ['user.read', 'contacts.read', 'mail.read', 'mail.read.shared', 'mail.send'],
};
